import { safeReturn } from './return-to';

/** Tiny history-based router. Routes: /, /record, /moment/:id, /practice/:id, /round (the daily round; /review, its old path, opens it too), /tend/… (a tended round), /land/:id (a moment lands in the town; #lesson opens its first lesson there), /opening (the first night's cold open and map), /exam, /start, /circle (beta; /circle/join/<code> opens its join consent with the code filled in), /friend/<secret> (beta; a friend link: "name wants to be friends", one tap to agree), /raid/new/:friend (?edit=<raid id> changes a loading army), /raid/spoils, /raid/tour (how raids work; ?next= a /raid/ path or /circle to go on to) and /raid/:id (raids, beta), /signin, /town-check (public), /join/:token, /recover/:token (beta); /tutor/:id (mothballed voice tutor, dev-only); /prototype/social, /prototype/town, /prototype/tech, /prototype/round, /prototype/battle, /prototype/brand, /prototype/arrival, /prototype/first-night, /prototype/sky (dev-only throwaways) */

export type Route =
  | { name: 'home' }
  | { name: 'record' }
  | { name: 'review' }
  | { name: 'exam' }
  | { name: 'start' }
  | { name: 'activity' }
  | { name: 'circle'; code?: string; returnTo?: string }
  | { name: 'friend'; token: string }
  | { name: 'raid-new'; friend: string; edit?: string }
  | { name: 'raid'; id: string; returnTo?: string }
  | { name: 'raid-spoils'; returnTo?: string }
  | { name: 'raid-tour'; next: string | null; context: string | null }
  | { name: 'signin' }
  | { name: 'town-check' }
  | { name: 'opening' }
  | { name: 'join'; token: string }
  | { name: 'recover'; token: string }
  | { name: 'moment'; id: string }
  | { name: 'practice'; id: string; returnTo: string }
  | { name: 'land'; id: string }
  | { name: 'tend'; tend: Tend }
  | { name: 'tutor'; id: string }
  | { name: 'prototype-social' }
  | { name: 'prototype-town' }
  | { name: 'prototype-tech' }
  | { name: 'prototype-round' }
  | { name: 'prototype-battle' }
  | { name: 'prototype-raid' }
  | { name: 'prototype-brand' }
  | { name: 'prototype-arrival' }
  | { name: 'prototype-first-night' }
  | { name: 'prototype-sky' };

/**
 * What a tended round practises (v1 ticket 27): a district's run, one building's card, or a shared word across a
 * road. A word's key is lesson text, so it rides in the query (`?key=`), which the server's request log leaves out.
 */
export type Tend =
  | { kind: 'district'; id: string }
  | { kind: 'word'; key: string }
  | { kind: 'road'; a: string; b: string; key: string };

/** The path of a tended round. */
export function tendPath(t: Tend): string {
  const q = (key: string) => `?key=${encodeURIComponent(key)}`;
  return t.kind === 'district' ? `/tend/district/${t.id}`
    : t.kind === 'word' ? `/tend/word${q(t.key)}`
    : `/tend/road/${t.a}/${t.b}${q(t.key)}`;
}

/** A practice entry from home can return to the moments sheet after the task. */
export function practicePath(id: string, returnTo = '/#moments'): string {
  const safe = safePracticeReturn(id, returnTo);
  return `/practice/${encodeURIComponent(id)}?return=${encodeURIComponent(safe)}`;
}

/** Keep practice exits inside the app and on destinations this surface actually provides. */
function safePracticeReturn(id: string, raw: string | null | undefined): string {
  const safe = safeReturn(raw);
  return safe === '/#moments' || safe === '/' || safe === `/moment/${id}` ? safe : `/moment/${id}`;
}

export function parse(pathname: string, search = ''): Route {
  const p = pathname.replace(/\/+$/, '') || '/';
  const key = new URLSearchParams(search).get('key');
  if (p === '/') return { name: 'home' };
  if (p === '/record') return { name: 'record' };
  // v1 ticket 15: the daily round replaces review; old /review links open the round.
  if (p === '/round' || p === '/review') return { name: 'review' };
  if (p === '/exam') return { name: 'exam' };
  if (p === '/start') return { name: 'start' };
  const returnTo = new URLSearchParams(search).get('next') === '/activity' ? '/activity' : undefined;
  if (p === '/activity') return { name: 'activity' };
  if (p === '/circle') return { name: 'circle', ...(returnTo ? { returnTo } : {}) };
  if (p === '/signin') return { name: 'signin' };
  if (p === '/town-check') return { name: 'town-check' };
  if (p === '/opening') return { name: 'opening' };
  // PROTOTYPE — throwaway social/gamification variants, never routed in production.
  if (import.meta.env.DEV && p === '/prototype/social') return { name: 'prototype-social' };
  if (import.meta.env.DEV && p === '/prototype/town') return { name: 'prototype-town' };
  if (import.meta.env.DEV && p === '/prototype/tech') return { name: 'prototype-tech' };
  if (import.meta.env.DEV && p === '/prototype/round') return { name: 'prototype-round' };
  if (import.meta.env.DEV && p === '/prototype/battle') return { name: 'prototype-battle' };
  if (import.meta.env.DEV && p === '/prototype/raid') return { name: 'prototype-raid' };
  if (import.meta.env.DEV && p === '/prototype/brand') return { name: 'prototype-brand' };
  if (import.meta.env.DEV && p === '/prototype/arrival') return { name: 'prototype-arrival' };
  if (import.meta.env.DEV && p === '/prototype/first-night') return { name: 'prototype-first-night' };
  if (import.meta.env.DEV && p === '/prototype/sky') return { name: 'prototype-sky' };
  let m = p.match(/^\/join\/([^/]+)$/);
  if (m) return { name: 'join', token: m[1] };
  // A circle link: the Friends screen on its join consent, the code filled in (the server logs the path with the code masked).
  m = p.match(/^\/circle\/join\/([^/]+)$/);
  if (m) {
    try { return { name: 'circle', code: decodeURIComponent(m[1]) }; } catch { return { name: 'circle' }; }
  }
  // A friend link: the secret is only ever in this path (the server logs it masked) and the request bodies.
  m = p.match(/^\/friend\/([^/]+)$/);
  if (m) {
    try { return { name: 'friend', token: decodeURIComponent(m[1]) }; } catch { return { name: 'home' }; }
  }
  m = p.match(/^\/recover\/([^/]+)$/);
  if (m) return { name: 'recover', token: m[1] };
  // Raids (v1 ticket 47): the armoury for a friend, then one raid as it stands.
  m = p.match(/^\/raid\/new\/([^/]+)$/);
  // `?edit=<raid id>`: change the army of that unlaunched raid (its picks come back in the armoury).
  if (m) return { name: 'raid-new', friend: decodeURIComponent(m[1]), edit: new URLSearchParams(search).get('edit') ?? undefined };
  if (p === '/raid/spoils') return { name: 'raid-spoils', ...(returnTo ? { returnTo } : {}) };
  if (p === '/raid/tour') {
    const query = new URLSearchParams(search);
    return { name: 'raid-tour', next: query.get('next'), context: query.get('context') };
  }
  m = p.match(/^\/raid\/([^/]+)$/);
  if (m) return { name: 'raid', id: decodeURIComponent(m[1]), ...(returnTo ? { returnTo } : {}) };
  m = p.match(/^\/moment\/([^/]+)$/);
  if (m) return { name: 'moment', id: m[1] };
  m = p.match(/^\/practice\/([^/]+)$/);
  if (m) return { name: 'practice', id: m[1], returnTo: safePracticeReturn(m[1], new URLSearchParams(search).get('return')) };
  m = p.match(/^\/land\/([^/]+)$/);
  if (m) return { name: 'land', id: m[1] };
  m = p.match(/^\/tend\/district\/([^/]+)$/);
  if (m) return { name: 'tend', tend: { kind: 'district', id: m[1] } };
  if (p === '/tend/word' && key) return { name: 'tend', tend: { kind: 'word', key } };
  m = p.match(/^\/tend\/road\/([^/]+)\/([^/]+)$/);
  if (m && key) return { name: 'tend', tend: { kind: 'road', a: m[1], b: m[2], key } };
  // MOTHBALLED live voice tutor: dev-only until the voice exam rewires it.
  m = p.match(/^\/tutor\/([^/]+)$/);
  if (import.meta.env.DEV && m) return { name: 'tutor', id: m[1] };
  return { name: 'home' };
}

/**
 * Asked before the screen changes: the question to confirm when leaving now
 * would lose something (answers not saved yet), or null when nothing would be.
 */
export type LeaveGuard = () => string | null;

let leaveGuard: LeaveGuard | null = null;
let leaveAccepted: (() => void) | null = null;
/** Path, query and hash on show, so a refused navigation restores the active screen or capture panel. */
const here = () => location.pathname + location.search + location.hash;
let shownPath = typeof location !== 'undefined' ? here() : '/';
/** go() has asked already: its own popstate passes without asking again. */
let navigating = false;

const onBeforeUnload = (e: BeforeUnloadEvent) => {
  if (!leaveGuard?.()) return;
  // Closing or reloading the tab: browsers only show their own wording here.
  e.preventDefault();
  e.returnValue = true;
};

/** A screen with something at stake registers a guard while it is up; null clears it (on disconnect). */
export function setLeaveGuard(fn: LeaveGuard | null, onAccepted?: () => void) {
  if (fn && !leaveGuard) window.addEventListener('beforeunload', onBeforeUnload);
  if (!fn && leaveGuard) window.removeEventListener('beforeunload', onBeforeUnload);
  leaveGuard = fn;
  leaveAccepted = fn ? onAccepted ?? null : null;
}

export function mayLeave(): boolean {
  const question = leaveGuard?.();
  if (!question) return true;
  const accepted = confirm(question);
  if (accepted) leaveAccepted?.();
  return accepted;
}

/** `force` skips the leave guard: the session is gone, so nothing on screen can be saved any more. */
export function go(path: string, replace = false, force = false) {
  if (!force && !mayLeave()) return;
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  navigating = true;
  try {
    window.dispatchEvent(new PopStateEvent('popstate'));
  } finally {
    navigating = false;
  }
}

/**
 * For the app's popstate listener: may the screen follow the URL? go() has
 * asked already; a browser Back/Forward to another screen or panel asks the guard here,
 * and when the learner stays, the URL of the screen still on show comes back.
 */
export function acceptPop(): boolean {
  if (!navigating && here() !== shownPath && !mayLeave()) {
    history.pushState(null, '', shownPath);
    return false;
  }
  shownPath = here();
  return true;
}

export function back(fallback = '/') {
  if (history.length > 1) history.back();
  else go(fallback, true);
}
